import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { ErrorModal, useUTLSettingsContext } from "@dexteel/mesf-core";
import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid2 as Grid,
	Step,
	StepConnector,
	StepLabel,
	Stepper,
	Typography,
} from "@mui/material";
import { TextField } from "@mui/material";
import { ColDef, ValueGetterParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { get } from "lodash-es";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { getCatalogElements } from "../../repositories/CatalogElementRepository";
import { getCatalogResultSources } from "../../repositories/CatalogResultSourceRepository";
import { DateTimePickerDE } from "./DateTimePickerDE";
import { NotesFieldDE } from "./NotesFieldDE";
import { SampleSelector } from "./SampleSelector";

const steps = ["Sample Attributes", "Element Values", "Comments"] as const;

interface CreateSampleForm {
	SampleTypeId: number | null;
	SampleTypeName: string;
	Reported: Date | null;
	SampleSequence: number | null;
	Technician: string;
	Comments: string;
	ElementValues: Record<string, number | null>;
}

interface ElementInfo {
	ElementId: number;
	ElementName: string;
	Order: number;
	Decimals: number;
}

interface CreateSampleWizardProps {
	show: boolean;
	onHide: () => void;
	heatId: number | null;
	gradeId?: number | null;
	existingSamples?: { SampleTypeName: string; SampleSequence: number | null }[];
	elementData?: Array<{
		ElementName: string;
		ElementId: number;
		Order: number;
		Decimals: number;
	}>;
	onSampleCreated?: () => void;
}

export const CreateSampleWizard = ({
	show,
	onHide,
	heatId,
	gradeId,
	existingSamples = [],
	elementData = [],
	onSampleCreated,
}: CreateSampleWizardProps) => {
	const { state: utlSettingsState } = useUTLSettingsContext();
	const [activeStep, setActiveStep] = useState(0);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState<string>("");
	const [notificationMessage, setNotificationMessage] = useState<string>("");
	const [defaultResultSourceId, setDefaultResultSourceId] = useState<
		number | null
	>(null);
	const [gridValidationError, setGridValidationError] = useState<string | null>(
		null,
	);
	const [elements, setElements] = useState<ElementInfo[]>([]);
	const [elementNameToIdMap, setElementNameToIdMap] = useState<
		Record<string, number>
	>({});

	const gridRef = useRef<AgGridReact>(null);

	// Get max age days setting (default 30)
	const MAX_AGE_DAYS = useMemo(() => {
		const settingValue = get(
			utlSettingsState.settings,
			"LAB_DATA_ENTRY_SAMPLE_MAX_EDITABLE_AGE_TIME_DAYS",
			"30",
		);
		return Number(settingValue) || 30;
	}, [utlSettingsState.settings]);

	// Check if a sample date is too old
	const isSampleTooOld = useCallback(
		(reportedDate: Date | null): boolean => {
			if (!reportedDate) return false;
			const daysDiff =
				(Date.now() - new Date(reportedDate).getTime()) / (1000 * 60 * 60 * 24);
			return daysDiff > MAX_AGE_DAYS;
		},
		[MAX_AGE_DAYS],
	);

	// Get default ResultSourceId on mount
	useEffect(() => {
		const loadDefaultResultSource = async () => {
			try {
				const resp = await getCatalogResultSources();
				if (resp.ok) {
					const rows = get(resp, "data.tables[0].rows", []) as Array<{
						ResultSourceId: number;
						ResultSourceName: string;
						IsActive: boolean;
					}>;
					const activeSource = rows.find((r) => r.IsActive);
					if (activeSource) {
						setDefaultResultSourceId(activeSource.ResultSourceId);
					}
				}
			} catch (error) {
				console.error("Error loading result sources:", error);
			}
		};
		loadDefaultResultSource();
	}, []);

	// Load elements from elementData or catalog
	useEffect(() => {
		const loadElements = async () => {
			if (elementData && elementData.length > 0) {
				// Use elements from elementData (from current heat report)
				const elementList: ElementInfo[] = elementData.map((e) => ({
					ElementId: e.ElementId,
					ElementName: e.ElementName,
					Order: e.Order,
					Decimals: e.Decimals,
				}));
				setElements(elementList);
				const nameToIdMap: Record<string, number> = {};
				elementList.forEach((e) => {
					nameToIdMap[e.ElementName] = e.ElementId;
				});
				setElementNameToIdMap(nameToIdMap);
			} else {
				// Fallback to catalog
				try {
					const resp = await getCatalogElements();
					if (resp.ok) {
						const rows = get(resp, "data.tables[0].rows", []) as Array<{
							ElementId: number;
							ElementName: string;
							Order: number;
							Decimals: number;
							IsActive: boolean;
						}>;
						const activeElements = rows
							.filter((e) => e.IsActive)
							.map((e) => ({
								ElementId: e.ElementId,
								ElementName: e.ElementName,
								Order: e.Order,
								Decimals: e.Decimals,
							}));
						activeElements.sort(
							(a, b) =>
								a.Order - b.Order || a.ElementName.localeCompare(b.ElementName),
						);
						setElements(activeElements);
						const nameToIdMap: Record<string, number> = {};
						activeElements.forEach((e) => {
							nameToIdMap[e.ElementName] = e.ElementId;
						});
						setElementNameToIdMap(nameToIdMap);
					}
				} catch (error) {
					console.error("Error loading elements:", error);
				}
			}
		};
		if (show) {
			loadElements();
		}
	}, [show, elementData]);

	const createEmptyElementValues = (): Record<string, number | null> => {
		const emptyValues: Record<string, number | null> = {};
		elements.forEach((element) => {
			emptyValues[element.ElementName] = null;
		});
		return emptyValues;
	};

	const { control, handleSubmit, reset, setValue, watch } =
		useForm<CreateSampleForm>({
			defaultValues: {
				SampleTypeId: null,
				SampleTypeName: "",
				Reported: new Date(),
				SampleSequence: null,
				Technician: "",
				Comments: "",
				ElementValues: {},
			},
		});

	const sampleTypeName = watch("SampleTypeName");

	// Initialize element values when elements are loaded
	useEffect(() => {
		if (elements.length > 0 && show) {
			const emptyValues = createEmptyElementValues();
			setValue("ElementValues", emptyValues);
			setSingleRowData(emptyValues);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [elements, show]);

	const [singleRowData, setSingleRowData] = useState<
		Record<string, number | null>
	>({});

	// Reset wizard when shown/hidden
	useEffect(() => {
		if (show && elements.length > 0) {
			setActiveStep(0);
			setError("");
			setGridValidationError(null);
			const emptyValues = createEmptyElementValues();
			reset({
				SampleTypeId: null,
				SampleTypeName: "",
				Reported: new Date(),
				SampleSequence: null,
				Technician: "",
				Comments: "",
				ElementValues: emptyValues,
			});
			setSingleRowData(emptyValues);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [show, elements]);

	const handleSampleSelection = (
		value: number | null,
		sampleTypeName: string,
	) => {
		setValue("SampleTypeId", value);
		setValue("SampleTypeName", sampleTypeName);

		const matchingSamples = existingSamples.filter(
			(sample) => sample.SampleTypeName === sampleTypeName,
		);

		if (matchingSamples.length > 0) {
			const highestSequenceSample = matchingSamples.reduce((prev, current) => {
				const prevSequence = prev.SampleSequence || 0;
				const currentSequence = current.SampleSequence || 0;
				return currentSequence > prevSequence ? current : prev;
			});

			const nextOrder = (highestSequenceSample.SampleSequence || 0) + 1;
			setValue("SampleSequence", nextOrder);
		} else {
			setValue("SampleSequence", 1);
		}
	};

	// Calculate required fields: first 5 elements ordered by Order property
	const requiredFields = useMemo(() => {
		if (elements.length === 0) return [];
		const sortedElements = [...elements].sort((a, b) => a.Order - b.Order);
		return sortedElements.slice(0, 5).map((e) => e.ElementName);
	}, [elements]);

	const validateGridData = (): boolean => {
		const rowData = gridRef.current?.api?.getDisplayedRowAtIndex(0)?.data;
		const areRequiredFieldsFilled =
			rowData &&
			requiredFields.length > 0 &&
			requiredFields.every((field) => {
				return (
					rowData.hasOwnProperty(field) &&
					rowData[field] !== null &&
					rowData[field] !== undefined &&
					rowData[field] !== ""
				);
			});

		if (!areRequiredFieldsFilled) {
			setGridValidationError(
				`Please fill in the required fields: ${requiredFields.join(", ")}.`,
			);
		} else {
			setGridValidationError(null);
		}

		return areRequiredFieldsFilled || false;
	};

	const handleNext = () => {
		if (activeStep === 1) {
			const isValid = validateGridData();
			if (!isValid) return;

			const gridData =
				gridRef.current?.api?.getDisplayedRowAtIndex(0)?.data || singleRowData;
			setValue("ElementValues", gridData);
		}

		setActiveStep((prevActiveStep) => prevActiveStep + 1);
	};

	const handleBack = () => {
		setActiveStep((prevActiveStep) => {
			if (prevActiveStep === 1) {
				setGridValidationError(null);
			}
			return prevActiveStep - 1;
		});
	};

	const handleComplete = async (data: CreateSampleForm) => {
		if (activeStep === 1) {
			const gridData =
				gridRef.current?.api?.getDisplayedRowAtIndex(0)?.data || singleRowData;
			data.ElementValues = gridData;
		}

		if (!heatId) {
			setError("Heat ID is required.");
			return;
		}

		if (!gradeId) {
			setError(
				"No grade assigned. A grade (Cast Grade or Programmed Grade) must be assigned first.",
			);
			return;
		}

		if (!data.SampleTypeId) {
			setError("Sample Code is required.");
			return;
		}

		if (!defaultResultSourceId) {
			setError("Result Source is not available. Please try again.");
			return;
		}

		// Check if sample date is too old
		if (isSampleTooOld(data.Reported)) {
			setError(
				`Cannot create sample: The reported date is older than ${MAX_AGE_DAYS} days. Samples older than ${MAX_AGE_DAYS} days cannot be created.`,
			);
			return;
		}

		setIsSubmitting(true);
		setError("");

		try {
			const { upsertChemistrySample, upsertChemistryResult } = await import(
				"../../repositories/ChemistrySampleRepository"
			);

			// Step 1: Create the sample
			const sampleResponse = await upsertChemistrySample({
				Id: null, // New sample
				SampleTypeId: data.SampleTypeId,
				SampleSequence: data.SampleSequence,
				DateTimeResults: data.Reported,
				StatusCode: "V", // Valid status
				ResultSourceId: defaultResultSourceId,
				Comments: data.Comments || "",
				HeatId: heatId,
				Technician: data.Technician || "",
				Melter: "",
				// Required fields for ChemistrySample interface
				DateTimeReceived: null,
				DateResults: null,
				SampleName: "",
				DateError: false,
				DateString: "",
				ElementsId: null,
				ElementsValue: null,
				ResultsId: null,
				elementParams: [],
				sampleParams: [],
				isNew: null,
			} as any);

			if (!sampleResponse.ok) {
				setError(sampleResponse.message || "Failed to create sample.");
				return;
			}

			// Get ChemistrySampleId from response
			// UpsertChemistrySample returns @ID AS Id in the response
			let chemistrySampleId: number | null = null;
			if (sampleResponse.data?.tables?.[0]?.rows?.[0]) {
				chemistrySampleId = sampleResponse.data.tables[0].rows[0].Id;
			}

			if (!chemistrySampleId) {
				setError("Failed to get sample ID after creation.");
				return;
			}

			// Step 2: Create chemistry results for each element
			const elementValues = data.ElementValues || {};
			const results: Promise<any>[] = [];

			for (const [elementName, value] of Object.entries(elementValues)) {
				if (value !== null && value !== undefined) {
					const elementId = elementNameToIdMap[elementName];
					if (elementId) {
						results.push(
							upsertChemistryResult({
								Id: null,
								ChemistrySampleId: chemistrySampleId,
								ElementId: elementId,
								Value: Number(value),
							}),
						);
					}
				}
			}

			// Wait for all results to be created
			const resultResponses = await Promise.all(results);
			const failedResults = resultResponses.filter((r) => !r.ok);

			if (failedResults.length > 0) {
				setError(
					`Sample created but some chemistry results failed: ${failedResults[0].message}`,
				);
				return;
			}

			// Success
			setNotificationMessage("Sample created successfully");
			reset({
				SampleTypeId: null,
				SampleTypeName: "",
				Reported: new Date(),
				SampleSequence: null,
				Technician: "",
				Comments: "",
				ElementValues: createEmptyElementValues(),
			});
			setSingleRowData(createEmptyElementValues());
			setActiveStep(0);
			if (onSampleCreated) {
				onSampleCreated();
			}
			onHide();
		} catch (error: any) {
			setError(error.message || "An error occurred while creating the sample.");
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleCancel = () => {
		reset({
			SampleTypeId: null,
			SampleTypeName: "",
			Reported: new Date(),
			SampleSequence: null,
			Technician: "",
			Comments: "",
			ElementValues: createEmptyElementValues(),
		});
		setSingleRowData(createEmptyElementValues());
		setActiveStep(0);
		setError("");
		setGridValidationError(null);
		onHide();
	};

	// Generate element columns for AG-Grid
	const elementColumns: ColDef[] = useMemo(() => {
		return elements.map((element) => ({
			field: element.ElementName,
			headerName:
				element.ElementName === "FEPercentage" ? "FE%" : element.ElementName,
			flex: 1,
			minWidth: 80,
			editable: true,
			suppressHeaderMenuButton: true,
			cellEditor: "agNumberCellEditor",
			cellEditorParams: {
				min: 0,
				max: 99.9999,
				step: Math.pow(10, -element.Decimals),
				precision: element.Decimals,
				showStepperButtons: true,
			},
			valueGetter: (params: ValueGetterParams) => {
				return params?.data?.[element.ElementName];
			},
			cellStyle: {
				display: "flex",
				justifyContent: "center",
				alignItems: "center",
				boxSizing: "border-box",
			},
		}));
	}, [elements]);

	const defaultColDef: ColDef = {
		flex: 1,
		minWidth: 80,
		resizable: false,
		sortable: false,
		filter: false,
		suppressHeaderMenuButton: true,
	};

	return (
		<>
			<Dialog fullWidth maxWidth="md" open={show} onClose={handleCancel}>
				<DialogTitle sx={{ padding: "5px 24px 10px" }}>
					<Typography
						variant="h5"
						sx={{
							margin: "10px 0",
							fontWeight: 600,
							userSelect: "none",
							marginLeft: "5px",
						}}
					>
						{activeStep === 0 && "New Sample - Attributes"}
						{activeStep === 1 && "New Sample - Elements"}
						{activeStep === 2 && "New Sample - Comments"}
					</Typography>
				</DialogTitle>

				<DialogContent sx={{ padding: "10px 24px" }} dividers>
					<Stepper
						activeStep={activeStep}
						alternativeLabel
						sx={{ background: "none" }}
						connector={
							<StepConnector
								sx={{
									"&.MuiStepConnector-alternativeLabel": {
										top: 22,
									},
									"&.Mui-active": {
										"& .MuiStepConnector-line": {
											backgroundColor: "#3f51b5",
										},
									},
									"&.Mui-completed": {
										"& .MuiStepConnector-line": {
											backgroundColor: "#4caf50",
										},
									},
									"& .MuiStepConnector-line": {
										height: 3,
										border: 0,
										backgroundColor: "#eaeaf0",
										borderRadius: 1,
									},
								}}
							/>
						}
					>
						{steps.map((label) => (
							<Step key={label}>
								<StepLabel
									slotProps={{
										stepIcon: {
											sx: {
												color: (theme) => theme.palette.primary.main,
												background: (theme) => theme.palette.background.paper,
												"&.Mui-completed": {
													color: (theme) => theme.palette.success.main,
												},
											},
										},
									}}
								>
									{label}
								</StepLabel>
							</Step>
						))}
					</Stepper>

					<form
						onSubmit={handleSubmit(handleComplete)}
						style={{ width: "100%" }}
					>
						{activeStep === 0 && (
							<Box mt={3}>
								<Grid
									container
									spacing={3}
									style={{ display: "flex", justifyContent: "center" }}
								>
									<Grid size={{ xs: 12, sm: 4, md: 4 }}>
										<Grid container direction="column" spacing={2}>
											<Grid size={{ xs: 12 }}>
												<Controller
													name="Reported"
													control={control}
													rules={{ required: "Date & Time is required." }}
													render={({ field, fieldState: { error } }) => (
														<DateTimePickerDE
															label="Date & Time"
															value={field.value}
															format="MM/DD/YYYY HH:mm"
															onChange={field.onChange}
															onBlur={() => {}}
															fieldError={error}
														/>
													)}
												/>
											</Grid>
											<Grid size={{ xs: 12 }}>
												<Controller
													name="SampleTypeId"
													control={control}
													rules={{ required: "Sample Code is required." }}
													render={({ field, fieldState: { error } }) => (
														<SampleSelector
															value={field.value}
															onChange={(newValue, newSampleTypeName) => {
																field.onChange(newValue);
																handleSampleSelection(
																	newValue,
																	newSampleTypeName,
																);
															}}
															fieldError={error}
														/>
													)}
												/>
											</Grid>
										</Grid>
									</Grid>

									<Grid size={{ xs: 12, sm: 4, md: 4 }}>
										<Grid container direction="column" spacing={2}>
											<Grid size={{ xs: 12 }}>
												<Controller
													name="Technician"
													control={control}
													rules={{
														required: "Technician is required.",
														maxLength: 50,
													}}
													render={({ field, fieldState: { error } }) => (
														<TextField
															label="Technician"
															variant="outlined"
															size="small"
															fullWidth
															value={field.value ?? ""}
															error={!!error}
															helperText={error?.message}
															onChange={field.onChange}
														/>
													)}
												/>
											</Grid>
											<Grid size={{ xs: 12 }}>
												<Controller
													name="SampleSequence"
													control={control}
													render={({ field, fieldState: { error } }) => (
														<TextField
															label="Order"
															variant="outlined"
															size="small"
															fullWidth
															type="number"
															value={field.value ?? ""}
															error={!!error}
															helperText={error?.message}
															onChange={field.onChange}
															disabled={true}
														/>
													)}
												/>
											</Grid>
										</Grid>
									</Grid>
								</Grid>
							</Box>
						)}

						{activeStep === 1 && (
							<Box
								sx={{
									height: "82px",
									width: "100%",
									marginTop: (theme) => theme.spacing(2),
									marginBottom: (theme) => theme.spacing(4),
									"& .ag-cell-value": {
										borderRight: "1px solid #e0e0e0",
									},
									"& .ag-row .ag-cell:last-child .ag-cell-value": {
										borderRight: "none",
									},
								}}
							>
								<AgGridReact
									ref={gridRef}
									rowData={[singleRowData]}
									rowHeight={30}
									headerHeight={30}
									columnDefs={elementColumns}
									defaultColDef={defaultColDef}
									animateRows={true}
									onCellValueChanged={(params) => {
										setSingleRowData(params.data);
									}}
									suppressContextMenu={true}
									singleClickEdit={true}
								/>

								<Box sx={{ minHeight: 20, margin: "4px 0" }}>
									<Typography sx={{ fontSize: "0.8rem", color: "red" }}>
										{gridValidationError ?? " "}
									</Typography>
								</Box>
							</Box>
						)}

						{activeStep === 2 && (
							<Box mt={2}>
								<Controller
									name="Comments"
									control={control}
									render={({ field }) => (
										<NotesFieldDE
											value={field.value ?? ""}
											onChange={field.onChange}
											placeholder="Add your comments here..."
											minRows={3}
											maxRows={3}
										/>
									)}
								/>
							</Box>
						)}

						{error && (
							<Box mt={2}>
								<Typography color="error" variant="body2">
									{error}
								</Typography>
							</Box>
						)}

						<DialogActions sx={{ margin: "15px 0", padding: "0 10px" }}>
							<Grid container spacing={1}>
								<Grid
									size={{ xs: 4 }}
									sx={{ marginRight: "auto", padding: "8px 10px" }}
								>
									<Button
										fullWidth
										sx={{
											padding: "6px 16px",
											fontSize: "0.875rem",
											lineHeight: 1.75,
											borderRadius: "4px",
										}}
										variant="contained"
										color="inherit"
										onClick={handleCancel}
										disabled={isSubmitting}
									>
										Cancel
									</Button>
								</Grid>

								{activeStep > 0 && (
									<Grid size={{ xs: 4 }} sx={{ padding: "8px 10px" }}>
										<Button
											fullWidth
											sx={{
												padding: "6px 16px",
												fontSize: "0.875rem",
												lineHeight: 1.75,
												borderRadius: "4px",
											}}
											variant="outlined"
											color="secondary"
											onClick={handleBack}
											disabled={isSubmitting}
										>
											Back
										</Button>
									</Grid>
								)}

								<Grid size={{ xs: 4 }} sx={{ padding: "8px 10px" }}>
									<Button
										fullWidth
										sx={{
											padding: "6px 16px",
											fontSize: "0.875rem",
											lineHeight: 1.75,
											borderRadius: "4px",
										}}
										variant="contained"
										color="primary"
										onClick={
											activeStep === steps.length - 1
												? handleSubmit(handleComplete)
												: handleSubmit(() => handleNext())
										}
										disabled={isSubmitting}
										startIcon={
											isSubmitting ? <CircularProgress size={24} /> : null
										}
									>
										{activeStep === steps.length - 1 ? "Save" : "Next"}
									</Button>
								</Grid>
							</Grid>
						</DialogActions>
					</form>
				</DialogContent>
			</Dialog>
			<NotificationSnackBar
				message={notificationMessage}
				onHide={() => setNotificationMessage("")}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
